import { useCallback, useState } from 'react';
import {
  TITLE_BASE_PRICE,
  TITLE_COLOR_PALETTE,
  TITLE_COLOR_PRICE,
  TITLE_EDIT_PRICE,
} from '@/data/titleColorPalette';
import { useLocale } from '@/context/LocaleContext';
import { useSilkBalance } from '@/hooks/useSilkBalance';
import { useTitleManagerMutations, useTitles, type TitleRow } from '@/hooks/useTitleManager';
import { BuyTitleModal } from './BuyTitleModal';
import { EditTitleModal } from './EditTitleModal';
import { isRowActive, notifyFromResult, type TmNotifyType } from './titleManagerUtils';

interface TitleTabProps {
  onNotify: (msg: string, type?: TmNotifyType) => void;
}

function isDarkHex(hex: string | null | undefined): boolean {
  if (!hex) return false;
  const raw = hex.replace('#', '').trim();
  if (raw.length !== 3 && raw.length !== 6) return false;
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return false;
  const [red, green, blue] = [r, g, b].map((value) => {
    const channel = value / 255;
    return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return (0.2126 * red + 0.7152 * green + 0.0722 * blue + 0.05) / 0.054 < 4.5;
}

export function TitleTab({ onNotify }: TitleTabProps) {
  const { t } = useLocale();
  const { data: titles = [], isLoading } = useTitles();
  const { testTitle, buyTitle, editTitle, applyTitle, removeTitle, reloadTitles } =
    useTitleManagerMutations();

  const [titleInput, setTitleInput] = useState('');
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [inputError, setInputError] = useState(false);
  const [paletteError, setPaletteError] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [editRow, setEditRow] = useState<TitleRow | null>(null);

  const editOpen = editRow !== null;
  const { data: balance, isLoading: balanceLoading, refetch: refetchBalance } = useSilkBalance(
    buyOpen || editOpen,
  );

  const buyColor = selectedColor !== null;
  const color = selectedColor ?? '#FFFFFF';
  const buyPrice = TITLE_BASE_PRICE + (buyColor ? TITLE_COLOR_PRICE : 0);

  const openBuyModal = useCallback(async () => {
    const trimmed = titleInput.trim();
    if (!trimmed) {
      setInputError(true);
      window.setTimeout(() => setInputError(false), 2000);
      return;
    }
    await refetchBalance();
    setBuyOpen(true);
  }, [refetchBalance, titleInput]);

  const openEditModal = useCallback(
    async (row: TitleRow) => {
      setEditRow(row);
      await refetchBalance();
    },
    [refetchBalance],
  );

  const handleTest = useCallback(async () => {
    const trimmed = titleInput.trim();
    if (!trimmed) {
      setInputError(true);
      window.setTimeout(() => setInputError(false), 2000);
      return;
    }
    if (!selectedColor) {
      setPaletteError(true);
      window.setTimeout(() => setPaletteError(false), 2000);
      return;
    }
    try {
      const result = await testTitle.mutateAsync({ title: trimmed, color: selectedColor });
      notifyFromResult(result, t, onNotify, 'tm_error_testing_title');
    } catch {
      onNotify(t('tm_error_testing_title'), 'danger');
    }
  }, [onNotify, selectedColor, t, testTitle, titleInput]);

  const handleBuyConfirm = useCallback(async () => {
    const trimmed = titleInput.trim();

    try {
      const result = await buyTitle.mutateAsync({
        title: trimmed,
        color,
        buy_color: buyColor,
      });
      if (result.success) {
        setBuyOpen(false);
        setTitleInput('');
        setSelectedColor(null);
        await reloadTitles();
      }
      notifyFromResult(result, t, onNotify, 'tm_error_buying_title');
    } catch {
      onNotify(t('tm_error_buying_title'), 'danger');
    }
  }, [buyColor, buyTitle, color, onNotify, reloadTitles, t, titleInput]);

  const handleEditConfirm = useCallback(
    async (payload: { title: string; color: string }) => {
      if (!editRow) return;
      try {
        const result = await editTitle.mutateAsync({
          title_id: editRow.id,
          title: payload.title,
          color: payload.color,
        });
        if (result.success) {
          setEditRow(null);
          await reloadTitles();
        }
        notifyFromResult(result, t, onNotify, 'tm_error_editing_title');
      } catch {
        onNotify(t('tm_error_editing_title'), 'danger');
      }
    },
    [editRow, editTitle, onNotify, reloadTitles, t],
  );

  const handleApply = useCallback(
    async (row: TitleRow) => {
      try {
        const result = await applyTitle.mutateAsync(row.id);
        if (result.success) await reloadTitles();
        notifyFromResult(result, t, onNotify, 'tm_error_applying_title');
      } catch {
        onNotify(t('tm_error_applying_title'), 'danger');
      }
    },
    [applyTitle, onNotify, reloadTitles, t],
  );

  const handleRemove = useCallback(
    async (row: TitleRow) => {
      try {
        const result = await removeTitle.mutateAsync(row.id);
        if (result.success) {
          if (editRow?.id === row.id) setEditRow(null);
          await reloadTitles();
        }
        notifyFromResult(result, t, onNotify, 'tm_error_removing_title');
      } catch {
        onNotify(t('tm_error_removing_title'), 'danger');
      }
    },
    [editRow?.id, onNotify, reloadTitles, removeTitle, t],
  );

  return (
    <>
      <div className="titlemanager-content">
        <div className="titlemanager-grid">
          <div className="titlemanager-list">
            <table className="titlemanager-table">
              <thead>
                <tr>
                  <th>{t('tm_title_column')}</th>
                  <th>{t('tm_actions_column')}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={2} className="tm-empty-cell">
                      {t('loading_data')}
                    </td>
                  </tr>
                ) : titles.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="tm-empty-cell">
                      {t('tm_no_titles')}
                    </td>
                  </tr>
                ) : (
                  titles.map((row) => {
                    const active = isRowActive(row.isactive);
                    const hex = row.color_hex || '#FFFFFF';
                    const dark = isDarkHex(hex);
                    return (
                      <tr key={row.id} className={active ? 'tm-row-active' : undefined}>
                        <td>
                          <div className="title-item">
                            <span
                              className="title-text"
                              style={{ color: dark ? '#e9dfcf' : hex }}
                            >
                              {row.title}
                            </span>
                            {dark ? <span className="tm-title-color-sample" style={{ backgroundColor: hex }} title={hex} aria-label={hex} /> : null}
                          </div>
                        </td>
                        <td>
                          <div className="titlemanager-actions">
                            <button
                              type="button"
                              className="titlemanager-action-btn"
                              title={t('tm_apply_title')}
                              aria-label={t('tm_apply_title')}
                              disabled={active || applyTitle.isPending}
                              onClick={() => void handleApply(row)}
                            >
                              <i className="fas fa-check" aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="titlemanager-action-btn"
                              title={t('tm_edit')}
                              aria-label={t('tm_edit')}
                              disabled={editTitle.isPending}
                              onClick={() => void openEditModal(row)}
                            >
                              <i className="fas fa-pen" aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="titlemanager-action-btn"
                              title={t('tm_remove_title')}
                              aria-label={t('tm_remove_title')}
                              disabled={removeTitle.isPending}
                              onClick={() => void handleRemove(row)}
                            >
                              <i className="fas fa-trash" aria-hidden />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="titlemanager-editor">
            <label className="tm-editor-label" htmlFor="custom-title-input">{t('tm_title_column')}</label>
            <div className="titlemanager-input-group">
              <input
                id="custom-title-input"
                type="text"
                className={`titlemanager-input${inputError ? ' tm-input-error' : ''}${
                  isDarkHex(selectedColor) ? ' title-text--dark' : ''
                }`}
                placeholder={t('tm_enter_title')}
                maxLength={64}
                value={titleInput}
                style={{ color: selectedColor ?? '#FFFFFF' }}
                onChange={(e) => setTitleInput(e.target.value)}
              />
              <button
                type="button"
                className="titlemanager-btn"
                disabled={testTitle.isPending}
                onClick={() => void handleTest()}
              >
                {testTitle.isPending ? t('tm_processing') : t('tm_test')}
              </button>
              <button
                type="button"
                className="titlemanager-btn"
                disabled={buyTitle.isPending}
                onClick={() => void openBuyModal()}
              >
                {buyTitle.isPending ? t('tm_processing') : t('tm_buy')}
              </button>
            </div>

            <div className="titlemanager-prices">
              <div className="price-item">
                <span>{t('tm_color_optional')}:</span>
                <span className="price-amount">+{TITLE_COLOR_PRICE} Silks</span>
              </div>
              <div className="price-item price-item-total">
                <span>{t('tm_total_pay')}:</span>
                <span className="price-amount">{buyPrice} Silks</span>
              </div>
              <div className="price-item tm-edit-price-note">
                <span>{t('tm_edit_separate')}:</span>
                <span>{TITLE_EDIT_PRICE} Silks</span>
              </div>
            </div>
            <div className={`titlemanager-color-palette${paletteError ? ' tm-palette-error' : ''}`}>
              <div className="color-grid">
                {TITLE_COLOR_PALETTE.map((c) => (
                  <button
                    type="button"
                    key={c.hex}
                    className={`color-option${selectedColor === c.hex ? ' selected' : ''}`}
                    data-color={c.hex}
                    style={{ backgroundColor: c.hex }}
                    title={c.label}
                    aria-label={c.label}
                    aria-pressed={selectedColor === c.hex}
                    onClick={() => setSelectedColor(c.hex)}
                  />
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      <BuyTitleModal
        open={buyOpen}
        title={titleInput.trim()}
        color={color}
        buyColor={buyColor}
        price={buyPrice}
        balance={balance ?? null}
        balanceLoading={balanceLoading}
        confirming={buyTitle.isPending}
        onConfirm={() => void handleBuyConfirm()}
        onClose={() => {
          if (!buyTitle.isPending) setBuyOpen(false);
        }}
      />

      <EditTitleModal
        open={editOpen}
        row={editRow}
        balance={balance ?? null}
        balanceLoading={balanceLoading}
        confirming={editTitle.isPending}
        onConfirm={(payload) => void handleEditConfirm(payload)}
        onClose={() => {
          if (!editTitle.isPending) setEditRow(null);
        }}
      />
    </>
  );
}
